import { sanitize } from "isomorphic-dompurify";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { useEffect, useRef, useState } from "react";
import { ToolTipType } from "../../utils/baseTypes";

const Tooltip = ({}) => {
  const [tooltipData, setTooltipData] = useState<ToolTipType>({
    title: "",
    excerpt: "",
    link: "",
  });
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const isHovering = useRef<boolean>(false);

  useEffect(() => {
    const linkList: HTMLAnchorElement[] = [];

    const handleLinkHover = async (event: Event) => {
      event.preventDefault();
      const { target } = event;
      isHovering.current = true;
      setShowTooltip(true);

      if (
        target instanceof HTMLAnchorElement &&
        target.classList.contains("encyclopedia")
      ) {
        const url = new URL(target.href);
        const slug = url?.pathname
          .split("/")
          .filter((segment) => segment !== "")
          .pop();
        // Fetch data for the encyclopedia custom post type
        try {
          const data = target.getAttribute("data-content");
          const content = data ? JSON.parse(data as string) : undefined;
          if (content && isHovering.current) {
            setTooltipData(content);
            return;
          }
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      }
    };
    const handleLinkLeave = (event: Event) => {
      event.preventDefault();
      isHovering.current = false;
      setShowTooltip(false);
    };
    const handleLinkFocus = (event: Event) => {
      // Timeout is needed, otherwise the tooltip might not be displayed at the correct position
      setTimeout(() => {
        handleLinkHover(event);
      }, 1);
    };
    const handleLinkBlur = (event: Event) => {
      handleLinkLeave(event);
    };

    const detachListeners = () => {
      linkList.forEach((link: HTMLAnchorElement) => {
        link.removeEventListener("mouseenter", handleLinkHover);
        link.removeEventListener("mouseleave", handleLinkLeave);
        link.removeEventListener("focus", handleLinkFocus);
        link.removeEventListener("blur", handleLinkBlur);
      });
    };

    // Create an observer instance
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            const links =
              node.querySelectorAll<HTMLAnchorElement>("a.encyclopedia");
            links.forEach((link) => {
              if (!linkList.includes(link)) {
                link.addEventListener("mouseover", handleLinkHover);
                link.addEventListener("mouseout", handleLinkLeave);
                link.addEventListener("focus", handleLinkFocus);
                link.addEventListener("blur", handleLinkBlur);
                linkList.push(link);
              }
            });
          }
        });
      });
    });

    // Start observing the document with the configured parameters
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Initial setup for existing links
    document
      .querySelectorAll<HTMLAnchorElement>("a.encyclopedia")
      .forEach((link) => {
        link.addEventListener("mouseenter", handleLinkHover);
        link.addEventListener("mouseleave", handleLinkLeave);
        link.addEventListener("focus", handleLinkFocus);
        link.addEventListener("blur", handleLinkBlur);
        linkList.push(link);
      });

    //Glossar add EventsListeners

    return () => {
      observer.disconnect();
      detachListeners();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ReactTooltip
      clickable
      id="glossar-tooltip"
      anchorSelect={".encyclopedia"}
      place="top"
      className="glossar-tooltip"
      isOpen={showTooltip}
      afterHide={() => {
        setTooltipData({
          title: "",
          excerpt: "",
          link: "",
        });
      }}
    >
      {(tooltipData.title === "" || tooltipData.excerpt === "") && (
        <div className="loader" />
      )}

      {tooltipData.title && (
        <span
          className="tooltip-title"
          dangerouslySetInnerHTML={{ __html: sanitize(tooltipData.title) }}
        />
      )}
      {tooltipData.excerpt && (
        <span
          className="tooltip-content"
          dangerouslySetInnerHTML={{ __html: tooltipData.excerpt }}
        />
      )}
    </ReactTooltip>
  );
};

export default Tooltip;
